/**
 * Vuex Leaderboard Module
 * Manages leaderboard state, caching, and API calls
 * Supports global and country-specific leaderboards
 */

const leaderboardModule = {
  namespaced: true,

  state: {
    // Global leaderboard data
    globalLeaderboard: [],
    globalLoading: false,
    globalError: null,
    globalCachedAt: null,

    // Country leaderboard data
    countryLeaderboard: [],
    currentCountryCode: null,
    countryLoading: false,
    countryError: null,
    countryCachedAt: null,

    // User's position data
    userStats: null,
    userStatsLoading: false,
    userStatsError: null,
    userStatsCachedAt: null,

    // Cache duration in milliseconds (5 minutes)
    cacheDuration: 5 * 60 * 1000
  },

  mutations: {
    // Global leaderboard mutations
    SET_GLOBAL_LEADERBOARD(state, data) {
      state.globalLeaderboard = data
      state.globalCachedAt = Date.now()
    },

    SET_GLOBAL_LOADING(state, loading) {
      state.globalLoading = loading
    },

    SET_GLOBAL_ERROR(state, error) {
      state.globalError = error
    },

    // Country leaderboard mutations
    SET_COUNTRY_LEADERBOARD(state, data) {
      state.countryLeaderboard = data
      state.countryCachedAt = Date.now()
    },

    SET_CURRENT_COUNTRY_CODE(state, code) {
      state.currentCountryCode = code
    },

    SET_COUNTRY_LOADING(state, loading) {
      state.countryLoading = loading
    },

    SET_COUNTRY_ERROR(state, error) {
      state.countryError = error
    },

    // User stats mutations
    SET_USER_STATS(state, stats) {
      state.userStats = stats
      state.userStatsCachedAt = Date.now()
    },

    SET_USER_STATS_LOADING(state, loading) {
      state.userStatsLoading = loading
    },

    SET_USER_STATS_ERROR(state, error) {
      state.userStatsError = error
    },

    // Clear cache
    CLEAR_CACHE(state) {
      state.globalCachedAt = null
      state.countryCachedAt = null
      state.userStatsCachedAt = null
    }
  },

  actions: {
    /**
     * Fetch global leaderboard
     * @param {number} limit - Number of entries to fetch (default: 100)
     * @param {boolean} forceRefresh - Skip cache and force fresh data
     */
    async fetchGlobalLeaderboard({ commit, state }, { limit = 100, forceRefresh = false } = {}) {
      try {
        // Check cache first
        if (!forceRefresh && state.globalCachedAt) {
          const cacheAge = Date.now() - state.globalCachedAt
          if (cacheAge < state.cacheDuration) {
            console.log('[Leaderboard] Using cached global leaderboard')
            return { success: true, cached: true }
          }
        }

        commit('SET_GLOBAL_LOADING', true)
        commit('SET_GLOBAL_ERROR', null)

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/leaderboards/global?limit=${limit}`
        )

        if (!response.ok) {
          throw new Error('Failed to fetch global leaderboard')
        }

        const data = await response.json()

        if (data.success) {
          commit('SET_GLOBAL_LEADERBOARD', data.data)
        } else {
          throw new Error(data.error || 'Unknown error')
        }

        commit('SET_GLOBAL_LOADING', false)
        return { success: true, cached: false }

      } catch (error) {
        commit('SET_GLOBAL_ERROR', error.message)
        commit('SET_GLOBAL_LOADING', false)
        console.error('[Leaderboard] Error fetching global leaderboard:', error)
        return { success: false, error: error.message }
      }
    },

    /**
     * Fetch country-specific leaderboard
     * @param {string} countryCode - ISO 2-letter country code (e.g., 'FR', 'US')
     * @param {number} limit - Number of entries to fetch (default: 50)
     * @param {boolean} forceRefresh - Skip cache and force fresh data
     */
    async fetchCountryLeaderboard({ commit, state }, { countryCode, limit = 50, forceRefresh = false }) {
      try {
        // Validate country code
        if (!countryCode || !/^[A-Z]{2}$/.test(countryCode)) {
          throw new Error('Invalid country code. Must be 2 uppercase letters (e.g., FR, US)')
        }

        // Check cache first (only if same country)
        if (!forceRefresh && state.currentCountryCode === countryCode && state.countryCachedAt) {
          const cacheAge = Date.now() - state.countryCachedAt
          if (cacheAge < state.cacheDuration) {
            console.log(`[Leaderboard] Using cached ${countryCode} leaderboard`)
            return { success: true, cached: true }
          }
        }

        commit('SET_COUNTRY_LOADING', true)
        commit('SET_COUNTRY_ERROR', null)
        commit('SET_CURRENT_COUNTRY_CODE', countryCode)

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/leaderboards/${countryCode}?limit=${limit}`
        )

        if (!response.ok) {
          throw new Error(`Failed to fetch leaderboard for ${countryCode}`)
        }

        const data = await response.json()

        if (data.success) {
          commit('SET_COUNTRY_LEADERBOARD', data.data)
        } else {
          throw new Error(data.error || 'Unknown error')
        }

        commit('SET_COUNTRY_LOADING', false)
        return { success: true, cached: false }

      } catch (error) {
        commit('SET_COUNTRY_ERROR', error.message)
        commit('SET_COUNTRY_LOADING', false)
        console.error('[Leaderboard] Error fetching country leaderboard:', error)
        return { success: false, error: error.message }
      }
    },

    /**
     * Fetch current user's leaderboard stats
     * Requires authentication
     * @param {boolean} forceRefresh - Skip cache and force fresh data
     */
    async fetchUserStats({ commit, state, rootState }, { forceRefresh = false } = {}) {
      try {
        const token = rootState.auth.token
        if (!token) {
          throw new Error('Authentication required')
        }

        // Check cache first
        if (!forceRefresh && state.userStatsCachedAt) {
          const cacheAge = Date.now() - state.userStatsCachedAt
          if (cacheAge < state.cacheDuration) {
            console.log('[Leaderboard] Using cached user stats')
            return { success: true, cached: true }
          }
        }

        commit('SET_USER_STATS_LOADING', true)
        commit('SET_USER_STATS_ERROR', null)

        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/api/leaderboards/me`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch user stats')
        }

        const data = await response.json()

        if (data.success) {
          commit('SET_USER_STATS', data)
        } else {
          throw new Error(data.error || 'Unknown error')
        }

        commit('SET_USER_STATS_LOADING', false)
        return { success: true, cached: false }

      } catch (error) {
        commit('SET_USER_STATS_ERROR', error.message)
        commit('SET_USER_STATS_LOADING', false)
        console.error('[Leaderboard] Error fetching user stats:', error)
        return { success: false, error: error.message }
      }
    },

    /**
     * Clear all leaderboard cache
     */
    clearCache({ commit }) {
      commit('CLEAR_CACHE')
    },

    /**
     * Refresh all leaderboard data
     */
    async refreshAll({ dispatch, rootGetters }) {
      const promises = [
        dispatch('fetchGlobalLeaderboard', { forceRefresh: true })
      ]

      // Fetch user stats if authenticated
      if (rootGetters['auth/isAuthenticated']) {
        promises.push(dispatch('fetchUserStats', { forceRefresh: true }))
      }

      await Promise.all(promises)
    }
  },

  getters: {
    // Global leaderboard getters
    globalLeaderboard: state => state.globalLeaderboard,
    globalLoading: state => state.globalLoading,
    globalError: state => state.globalError,
    hasGlobalData: state => state.globalLeaderboard.length > 0,

    // Country leaderboard getters
    countryLeaderboard: state => state.countryLeaderboard,
    currentCountryCode: state => state.currentCountryCode,
    countryLoading: state => state.countryLoading,
    countryError: state => state.countryError,
    hasCountryData: state => state.countryLeaderboard.length > 0,

    // User stats getters
    userStats: state => state.userStats,
    userStatsLoading: state => state.userStatsLoading,
    userStatsError: state => state.userStatsError,
    hasUserStats: state => state.userStats !== null,

    // User position in global leaderboard
    userGlobalRank: state => state.userStats?.rank_global || null,

    // User position in specific country
    userCountryRank: (state) => (countryCode) => {
      if (!state.userStats || !state.userStats.country_ranks) return null
      const countryRank = state.userStats.country_ranks.find(
        cr => cr.country_code === countryCode
      )
      return countryRank?.rank || null
    },

    // Check if user is in top 3 globally
    isUserInTopThreeGlobal: state => {
      const rank = state.userStats?.rank_global
      return rank && rank <= 3
    },

    // Get user's position in current leaderboard display
    userPositionInLeaderboard: (state) => (type) => {
      if (!state.userStats) return null

      if (type === 'global') {
        return state.globalLeaderboard.find(
          entry => entry.user_id === state.userStats.user_id
        )
      } else if (type === 'country' && state.currentCountryCode) {
        return state.countryLeaderboard.find(
          entry => entry.user_id === state.userStats.user_id
        )
      }
      return null
    }
  }
}

export default leaderboardModule
